import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api';

// 钉钉OAuth配置
export interface DingtalkOAuth {
  enable: boolean;
  client_id: string;
  client_secret: string;
}

// 自定义OAuth配置
export interface CustomOAuth {
  enable: boolean;
  client_id: string;
  client_secret: string;
  authorize_url: string;
  access_token_url: string;
  userinfo_url: string;
  scopes: string[];
  id_field: string;
  name_field: string;
  avatar_field: string;
  email_field: string;
}

// 百智云OAuth配置
export interface BaizhiOAuth {
  enable: boolean;
  client_id: string;
  client_secret: string;
  issuer: string; // 正式环境：https://baizhi.cloud
}

// 系统设置接口
export interface Setting {
  id: string;
  enable_sso: boolean;
  force_two_factor_auth: boolean;
  disable_password_login: boolean;
  enable_auto_login: boolean;
  dingtalk_oauth?: DingtalkOAuth;
  custom_oauth?: CustomOAuth;
  baizhi_oauth?: BaizhiOAuth;
  base_url?: string;
  created_at: string;
  updated_at: string;
}

// 更新设置请求参数
export interface UpdateSettingRequest {
  enable_sso?: boolean;
  force_two_factor_auth?: boolean;
  disable_password_login?: boolean;
  enable_auto_login?: boolean;
  dingtalk_oauth?: Partial<DingtalkOAuth>;
  custom_oauth?: Partial<CustomOAuth>;
  baizhi_oauth?: Partial<BaizhiOAuth>;
  base_url?: string;
  [key: string]: unknown;
}

/**
 * 获取指定租户的系统设置
 * @param tenantId 租户ID
 * @returns 租户设置信息
 */
export const getTenantSettings = async (tenantId: string): Promise<Setting> => {
  return apiGet<Setting>(`/v1/admin/tenants/${tenantId}/settings`);
};

/**
 * 更新指定租户的系统设置
 * @param tenantId 租户ID
 * @param data 更新的设置数据
 * @returns 更新后的设置信息
 */
export const updateTenantSettings = async (
  tenantId: string,
  data: UpdateSettingRequest
): Promise<Setting> => {
  return apiPut<Setting>(`/v1/admin/tenants/${tenantId}/settings`, data);
};

/**
 * 获取当前登录用户的系统设置
 * @returns 当前租户的设置信息
 */
export const getCurrentSettings = async (): Promise<Setting> => {
  return apiGet<Setting>('/v1/settings');
};

/**
 * 更新当前登录用户的系统设置
 * @param data 更新的设置数据
 * @returns 更新后的设置信息
 */
export const updateCurrentSettings = async (
  data: UpdateSettingRequest
): Promise<Setting> => {
  return apiPut<Setting>('/v1/settings', data);
};

// ============ 模型配置相关接口 ============

// 模型参数配置
export interface ModelParam {
  context_window?: number;
  max_tokens?: number;
  r1_enabled?: boolean;
  support_images?: boolean;
  support_computer_use?: boolean;
  support_prompt_cache?: boolean;
  temperature?: number;
}

// 模型类型（前端展示用）
export type ModelType = 'Chat' | 'Embedding';

// 模型类型（后端 API 返回）
export type ApiModelType = 'chat' | 'embedding' | 'analysis';

// 模型配置
export interface ModelConfig {
  id: string;
  type: ApiModelType;
  provider: string;
  model_name: string;
  show_name?: string;
  description?: string;
  base_url?: string;
  api_key?: string;
  api_version?: string;
  api_header?: string;
  is_active: boolean;
  is_default: boolean;
  parameters?: ModelParam;
  created_at: number;
}

// 创建模型配置请求
export interface CreateModelReq {
  type: string; // 'chat' | 'embedding' | 'analysis'
  provider: string;
  model_name: string;
  show_name?: string;
  description?: string;
  base_url?: string;
  api_key: string;
  api_version?: string;
  api_header?: string;
  is_active?: boolean;
  is_default?: boolean;
  parameters?: ModelParam;
}

// 模型配置列表响应
export interface ListModelResp {
  configs: ModelConfig[];
}

/**
 * 创建模型配置
 * @param tenantId 租户ID
 * @param data 模型配置数据
 * @returns 创建的模型配置
 */
export const createModelConfig = async (
  tenantId: string,
  data: CreateModelReq
): Promise<ModelConfig> => {
  return apiPost<ModelConfig>(
    `/v1/admin/tenants/${tenantId}/model`,
    data as unknown as Record<string, unknown>
  );
};

/**
 * 获取租户的模型配置列表
 * @param tenantId 租户ID
 * @param type 可选，按模型类型筛选
 * @returns 模型配置列表
 */
export const getModelConfigList = async (
  tenantId: string,
  type?: ModelType
): Promise<ListModelResp> => {
  const params = type ? `?type=${type}` : '';
  return apiGet<ListModelResp>(
    `/v1/admin/tenants/${tenantId}/model/list${params}`
  );
};

// 更新模型配置请求
export interface UpdateModelReq {
  id: string;
  provider?: string;
  model_name?: string;
  show_name?: string;
  description?: string;
  base_url?: string;
  api_key?: string;
  api_version?: string;
  api_header?: string;
  is_active?: boolean;
  is_default?: boolean;
  parameters?: ModelParam;
}

/**
 * 更新模型配置
 * @param tenantId 租户ID
 * @param modelId 模型配置ID
 * @param data 更新的模型配置数据
 * @returns 更新后的模型配置
 */
export const updateModelConfig = async (
  tenantId: string,
  modelId: string,
  data: UpdateModelReq
): Promise<ModelConfig> => {
  return apiPut<ModelConfig>(
    `/v1/admin/tenants/${tenantId}/model/${modelId}`,
    data as unknown as Record<string, unknown>
  );
};

/**
 * 删除模型配置
 * @param tenantId 租户ID
 * @param modelId 模型配置ID
 */
export const deleteModelConfig = async (
  tenantId: string,
  modelId: string
): Promise<void> => {
  return apiDelete<void>(`/v1/admin/tenants/${tenantId}/model/${modelId}`);
};

/**
 * 重置租户的模型配置（清除所有已选模型）
 * @param tenantId 租户ID
 */
export const resetModelConfig = async (tenantId: string): Promise<void> => {
  return apiDelete<void>(`/v1/admin/tenants/${tenantId}/model/reset`);
};

// 测试模型配置请求
export interface CheckModelReq {
  provider: string;
  model: string;
  base_url: string;
  api_key?: string;
  api_version?: string;
  api_header?: string;
  type: 'chat' | 'embedding' | 'rerank' | 'llm';
  param?: ModelParam;
}

// 测试模型配置响应
export interface CheckModelRes {
  content?: string;
  error?: string;
}

/**
 * 测试模型配置连通性
 * @param data 测试请求数据
 * @returns 测试结果
 */
export const checkModelConfig = async (
  data: CheckModelReq
): Promise<CheckModelRes> => {
  return apiPost<CheckModelRes>(
    '/v1/admin/model/check',
    data as unknown as Record<string, unknown>
  );
};

// 获取支持的模型列表请求
export interface ModelListReq {
  provider: string;
  base_url: string;
  api_key?: string;
  api_header?: string;
  type: string;
}

// 模型列表项
export interface ModelListItem {
  model: string;
}

// 获取支持的模型列表响应
export interface ModelListRes {
  models?: ModelListItem[];
  error?: string;
}

/**
 * 获取指定 Provider 支持的模型列表
 * @param data 查询请求数据
 * @returns 模型列表
 */
export const getSupportedModels = async (
  data: ModelListReq
): Promise<ModelListRes> => {
  return apiPost<ModelListRes>(
    '/v1/admin/model/provider/supported',
    data as unknown as Record<string, unknown>
  );
};
